import { createGlobalStyle, css } from "styled-components";
import {
  blue,
  blueA,
  brown,
  redA,
  blackA,
  whiteA,
  yellowA,
  red,
  green,
  yellow,
  orange,
  blueDark,
  slate,
  teal,
  tomato,
  purple,
  crimson,
  mint, 
  mintA,
  purpleA,
  orangeA,
  crimsonA,
  violet,
} from '@radix-ui/colors';



export type FontSize = keyof typeof theme.font.size;

export type HeadingType = keyof typeof theme.font.heading;

export type FontWeight = keyof typeof theme.font.weight;

export type Color = keyof typeof theme.color;

export type FontColor = keyof typeof theme.font.color;

export type FontStyle = keyof typeof theme.font.style;

export type BoxShadow = keyof typeof theme.boxShadow;


const h1 = css`
  font-size: 4.8rem;
`;

const h2 = css`
  font-size: 3.4rem;
`;

const h3 = css`
  font-size: 2.4rem;
`;
const h4 = css`
  font-size: 2rem;
`;
const h5 = css`
  font-size: 1.6rem;
`;
const h6 = css`
  font-size: 1.4rem;
`;

const primaryColors = {
  primaryA1: "hsla(198, 84%, 30%, 0.1)",
  primaryA2: "hsla(198, 84%, 30%, 0.2)",
  primaryA3: "hsla(198, 84%, 30%, 0.3)",
  primaryA4: "hsla(198, 84%, 30%, 0.4)",
  primaryA5: "hsla(198, 84%, 30%, 0.5)",
  primaryA6: "hsla(198, 84%, 30%, 0.6)",
  primaryA7: "hsla(198, 84%, 30%, 0.7)",
  primaryA8: "hsla(198, 84%, 30%, 0.8)",
  primaryA9: "hsla(198, 84%, 30%, 0.9)",

  primary1:  "hsla(198, 36%, 86%, 1)",
  primary2:  "hsla(198, 37%, 72%, 1)",
  primary3:  "hsla(198, 36%, 58%, 1)",
  primary4:  "hsla(198, 37%, 51%, 1)",
  primary5:  "hsla(198, 46%, 44%, 1)",
  primary6:  "hsla(198, 61%, 37%, 1)",
  primary7:  "hsla(198, 84%, 30%, 1)",
  primary8: "hsla(198, 84%, 20%, 1)",
  primary9: "hsla(198, 85%, 18%, 1)",
  primary10: "hsla(198, 85%, 13%, 1)",
}


export const theme = {
  color: {
    ...blue,
    ...blueA,
    ...green,
    ...orange,
    ...yellow,
    ...yellowA,
    ...red,
    ...redA,
    ...slate,
    ...blackA,
    ...whiteA,
    ...blueDark,
    ...teal,
    ...tomato,
    ...crimson,
    ...yellow,
    ...purple,
    ...purpleA,
    ...primaryColors,
    ...mint,
    ...mintA,
    ...crimsonA,
    ...orangeA,
    ...violet,

    backgroundBody:     slate.slate2,
    backgroundCritical: red.red3,
    criticalBorder:     red.red5,
    backgroundPriority: orange.orange3,
    priorityBorder:     orange.orange5,
    primaryBorder:      slate.slate7,
    secondaryBorder:    slate.slate6,
    grayDarkest:        slate.slate12,
    grayDark:           slate.slate11,
    grayDefault:        slate.slate9,
    grayLight:          slate.slate4,
    grayLightest:       slate.slate3,
    splitViewGutter:    slate.slate4,

    alert:        tomato.tomato9,
    black:        slate.slate12,
    white:        "#FFFFFF",
    error:        red.red9,
    backdrop:     blackA.blackA9,
    disabled:     slate.slate5,
    disabledText: slate.slate9,
    hover:        blackA.blackA3,
    iconDefault:  slate.slate12,
    activeItem:   primaryColors.primaryA1,

    items: {
      deliverable: blue.blue6,
      meeting:     brown.brown9,
      question:    yellow.yellow8,
      milestone:   violet.violet6,
    },

    buttons: {
      primary: {
        background: primaryColors.primary7,
        hover:      primaryColors.primary8,
        active:     primaryColors.primary9,
        focus:      primaryColors.primaryA3,
      },
      outlined: {
        border: slate.slate8,
        hover:  slate.slate10,
        active: slate.slate8,
        focus:  primaryColors.primaryA3,
      },
      alert: {
        hover:  tomato.tomato10,
        active: tomato.tomato11,
        focus:  redA.redA7,
      }
    },
    sidebar: {
      background: slate.slate12,
      backgroundDark: '#000',
      footer:     primaryColors.primary8,
      hover:      primaryColors.primary10,
      active:     primaryColors.primary8,
      selected:   slate.slate12,
      border:     slate.slate6,
      text:       "#FFFFFF",
      icon:       "#FFFFFF",
    },
    input: {
      select: slate.slate5,
      hover:  slate.slate3,
      border: slate.slate7,
    },
    checkbox: {
      deliverable:    blue.blue6,
      delivHover:     blue.blue9,
      delivFocus:     blue.blue12,
      meeting:        brown.brown9,
      meetingHover:   brown.brown10,
      meetingFocus:   brown.brown11,
      question:       yellow.yellow8,
      questionHover:  yellow.yellow9,
      questionFocus:  yellowA.yellowA4,
      milestone:      violet.violet6,
      milestoneHover: violet.violet6,
      milestoneFocus: violet.violet5,
    },
    tab: {
      text:             primaryColors.primary8,
      background:       primaryColors.primaryA1,
      backgroundActive: primaryColors.primaryA2,
      counter:          primaryColors.primaryA2,
    },
    formElements :{
      border:          slate.slate7,
      hover:           slate.slate8,
      active:          slate.slate9,
      main:            slate.slate7,
      background:      primaryColors.primary7,
      focus:           primaryColors.primaryA2,
      radioButtonMain: primaryColors.primary7,
      checkboxMain:    primaryColors.primary7,
      toggleMain:      green.green9,
    },
    cards: {
      question:        yellow.yellow3,
      questionBorder:  yellow.yellow6,
      questionFooter:  yellow.yellow5,
      deliverable:     blue.blue3,
      delivBorder:     blue.blue6,
      delivFooter:     blue.blue5,
      meeting:         brown.brown9,
      meetingBorder:   brown.brown10,
      meetingFooter:   brown.brown11,
      milestone:       violet.violet3,
      milestoneBorder: violet.violet6,
      milestoneFooter: violet.violet5,
    },
    status: {
      delivered: green.green8,
      answered:  green.green8,
      success:   green.green8,
      archived:  green.green11,
      new:       orange.orange8,
      critical:  red.red10,
      none:      slate.slate10,
      active:    "#F59E0B",
      priority:  "#FB923C",
      online:    green.green8,
    },
  },
  layout: {
    gutter: "1vw",
    gutterSmall: ".33vw"
  },
  paper: {
    small: {
      maxWidth: "568px",
      justifyContent: "center"
    },
    regular: {
      maxWidth: "768px",
      justifyContent: "center"
    },
    large: {
      maxWidth: "1024px",
      justifyContent: "center"
    },
    fullwidth: {
      padding: "1vw auto auto",
      justifyContent: "center"
    }
  },
  boxShadow: {
    small: `1px 2px 4px rgba(0,0,0,0.11),
    2px 3px 5px rgba(0,0,0,0.11)`,
    hover: `1px 2px 4px rgba(0,0,0,0.11),
    2px 3px 5px rgba(0,0,0,0.11),
    4px 5px 7px rgba(0,0,0,0.11)`,
    medium: `0 1px 1px rgba(0,0,0,0.10), 
    0 2px 2px rgba(0,0,0,0.10), 
    0 4px 4px rgba(0,0,0,0.10), 
    0 6px 8px rgba(0,0,0,0.10)`,
    large: ` 0 1px 1px rgba(0,0,0,0.10), 
    0 2px 2px rgba(0,0,0,0.10), 
    0 4px 4px rgba(0,0,0,0.10), 
    0 8px 8px rgba(0,0,0,0.10), 
    0 16px 16px rgba(0,0,0,0.10),
    0 32px 32px rgba(0,0,0,0.10)`
  },
  font: {
    heading: {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
    },
    weight: {
      thin: "300",
      italic: "300",
      normal: "400",
      bold: "600",
      oblique: "600",
    },
    color: {
      ...slate,
      placeholder: slate.slate11,
      slate:       slate.slate11,
      black:       slate.slate12,
      blue:        blue.blue11,
      blueDark:    blue.blue12,
      green:       green.green11,
      greenDark:   green.green12,
      yellow:      yellow.yellow11,
      yellowDark:  yellow.yellow12,
      red:         red.red11,
      redDark:     red.red12,
      white:       "#FFFFFF",
    },
    size: {
      xxxsmall: "0.75rem",
      xxsmall: "0.9rem",
      xsmall: "1.2rem",
      small: "1.4rem",
      default: "1.6rem",
      large: "1.8rem"
    },
    style: {
      normal: "normal",
      bold:   "bold",
      italic: "italic",
      oblique: "oblique",
    },
  },
};

export const GlobalStyle = createGlobalStyle`

* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

  html, body, div, span, applet, object, iframe,
  h1, h2, h3, h4, h5, h6, p, blockquote, pre,
  a, abbr, acronym, address, big, cite, code,
  del, dfn, em, img, ins, kbd, q, s, samp,
  small, strike, strong, sub, sup, tt, var,
  b, u, i, center,
  dl, dt, dd, ol, ul, li,
  fieldset, form, label, legend,
  table, caption, tbody, tfoot, thead, tr, th, td,
  article, aside, canvas, details, embed, 
  figure, figcaption, footer, header, hgroup, 
  menu, nav, output, ruby, section, summary,
  time, mark, audio, video {
    margin: 0;
    padding: 0;
    border: 0;
    /* font: inherit; */
    vertical-align: baseline;
  }

  /* HTML5 display-role reset for older browsers */
  article, aside, details, figcaption, figure, 
  footer, header, hgroup, menu, nav, section {
    display: block;
  }
  
  /* body {
    line-height: 1;
  } */

  ol, ul {
    list-style: none;
  }

  blockquote, q {
    quotes: none;
  }

  blockquote:before, blockquote:after,
  q:before, q:after {
    content: '';
    content: none;
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
  
  html {
    font-size:  62.5%;
    min-height: 100vh;
  }

  body {
    background:       ${theme.color.backgroundBody};
    background-color: ${theme.color.backgroundBody};
    min-height:       100vh;
  }

  * {
    /* font-family: 'Lato', arial !important; */
    font-family: 'Open Sans', arial !important;
  }
  strong {
    font-weight: 600;
  }
  html {
      width: 100%;
      height: 100%;
      display: table;
  }

  body {
      width: 100%;
      display: table-cell;
      overflow-x: hidden;
  }

  html, body {
      margin: 0;
      padding: 0;
  }
  #root {
    height: 100vh; /* changed from 100% to 100vh. Did this break something? */
  }

  span {
    white-space: normal !important;
  }
`;
